import React from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { resyncCustomer } from '@src/Api';
import type { CustomerSyncData } from './types';

export const CustomerTab: React.FC<{ customers: CustomerSyncData[]; loading: boolean; onResync: () => Promise<void> }> = ({ customers, loading, onResync }) => {
  const pending = React.useRef(new Set<number>());
  const [activeIds, setActiveIds] = React.useState<number[]>([]);
  const [feedback, setFeedback] = React.useState<{ success: boolean; message: string } | null>(null);
  const refresh = React.useRef(onResync);
  refresh.current = onResync;

  const retry = async (customer: CustomerSyncData) => {
    if (pending.current.has(customer.id)) return;
    pending.current.add(customer.id);
    setActiveIds(Array.from(pending.current));
    setFeedback(null);
    try {
      const response = await resyncCustomer(customer.id);
      const success = response?.status === 200;
      setFeedback({ success, message: success ? response.data.message : response?.data?.error || 'Customer synchronization failed. Please try again.' });
    } catch {
      setFeedback({ success: false, message: 'Customer synchronization failed. Please try again.' });
    } finally {
      try {
        await refresh.current();
      } finally {
        pending.current.delete(customer.id);
        setActiveIds(Array.from(pending.current));
      }
    }
  };

  return (
    <>
      {feedback && <Alert variant={feedback.success ? 'success' : 'danger'}>{feedback.message}</Alert>}
      <Table striped responsive className="enhanced-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>BC customer number</th>
            <th>Rentman customer ID</th>
            <th>Customer status</th>
            <th>Event</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="table-loader">
                <Spinner className="data-loading-spinner" role="status">
                  <span className="visually-hidden">Loading customers…</span>
                </Spinner>
              </td>
            </tr>
          ) : customers.length === 0 ? (
            <tr>
              <td colSpan={6}>No customer synchronization attempts yet.</td>
            </tr>
          ) : (
            customers.map((customer) => (
              <tr key={customer.id}>
                <td>{new Date(customer.updated_at).toLocaleString('da-DK')}</td>
                <td>{customer.bc_customer_number ?? customer.bc_customer_id}</td>
                <td>{customer.rentman_customer_id ?? '—'}</td>
                <td>{customer.status}</td>
                <td>{customer.message ?? '—'}</td>
                <td>
                  {['Failed', 'Created', 'Updated', 'Skipped'].includes(customer.status) ? (
                    <Button disabled={activeIds.includes(customer.id)} onClick={() => void retry(customer)}>
                      {activeIds.includes(customer.id) ? 'Syncing…' : 'Re-sync'}
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  );
};
